import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShieldAlert, MessageSquare, Mail, Loader2, ArrowRight } from "lucide-react";
import { formatCountdownDisplay } from "@/lib/utils";

export default function ReportingChannelStep({
  channel = "sms",
  setChannel,
  restaurantMobile = "",
  restaurantEmail = "",
  resendCountdown = 0,
  sendingOtp = false,
  designation = "Manager",
  onSendOtp,
  onGoToOtp,
  onExit,
}) {
  return (
    <Card className="bg-transparent border-0 text-white w-full mx-auto shadow-none">
      <CardHeader className="flex flex-col justify-center items-center text-center p-0 pb-4">
        <div className="w-14 h-14 rounded-full bg-cyellow/10 border border-cyellow/30 flex items-center justify-center mb-3 text-cyellow shadow-lg shadow-cyellow/5">
          <ShieldAlert className="w-7 h-7" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyellow/10 text-cyellow border border-cyellow/30 mb-2">
          {designation} Security Verification
        </div>
        <CardTitle className="text-xl font-bold text-white">
          Verify Reporting Access
        </CardTitle>
        <CardDescription className="text-cwhite3 mt-1.5 text-sm leading-relaxed max-w-sm">
          Access to financial and performance reports requires One-Time Password (OTP) authorization. Choose where to receive the verification code.
        </CardDescription>
      </CardHeader>

      <CardContent className="p-0 space-y-4">
        <RadioGroup
          value={channel}
          onValueChange={(val) => {
            if (resendCountdown > 0) return;
            setChannel(val);
          }}
          disabled={resendCountdown > 0 || sendingOtp}
          className="gap-3 pt-1"
        >
          {/* SMS Option */}
          <label
            htmlFor="reporting-channel-sms"
            className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
              resendCountdown > 0
                ? "opacity-60 cursor-not-allowed"
                : "cursor-pointer"
            } ${
              channel === "sms"
                ? "border-cyellow bg-cyellow/10 text-white shadow-sm"
                : "border-neutral-800 bg-neutral-900/60 hover:bg-neutral-800/60 text-neutral-300"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`p-2.5 rounded-lg shrink-0 ${
                  channel === "sms"
                    ? "bg-cyellow text-black"
                    : "bg-neutral-800 text-neutral-400"
                }`}
              >
                <MessageSquare size={18} />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-semibold text-sm text-white">SMS</div>
                <div className="text-xs text-neutral-400 font-mono mt-0.5 truncate">
                  {restaurantMobile}
                </div>
              </div>
            </div>
            <RadioGroupItem
              value="sms"
              id="reporting-channel-sms"
              disabled={resendCountdown > 0 || sendingOtp}
            />
          </label>

          {/* Email Option */}
          <label
            htmlFor="reporting-channel-email"
            className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
              resendCountdown > 0
                ? "opacity-60 cursor-not-allowed"
                : "cursor-pointer"
            } ${
              channel === "email"
                ? "border-cyellow bg-cyellow/10 text-white shadow-sm"
                : "border-neutral-800 bg-neutral-900/60 hover:bg-neutral-800/60 text-neutral-300"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`p-2.5 rounded-lg shrink-0 ${
                  channel === "email"
                    ? "bg-cyellow text-black"
                    : "bg-neutral-800 text-neutral-400"
                }`}
              >
                <Mail size={18} />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-semibold text-sm text-white">Email</div>
                <div className="text-xs text-neutral-400 font-mono mt-0.5 truncate">
                  {restaurantEmail}
                </div>
              </div>
            </div>
            <RadioGroupItem
              value="email"
              id="reporting-channel-email"
              disabled={resendCountdown > 0 || sendingOtp}
            />
          </label>
        </RadioGroup>

        {resendCountdown > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center space-y-1.5">
            <p className="text-xs text-amber-300">
              OTP already sent. Please wait{" "}
              <span className="font-mono font-bold text-amber-200">
                {formatCountdownDisplay(resendCountdown)}
              </span>{" "}
              before requesting a new code.
            </p>
            <div>
              <button
                type="button"
                onClick={onGoToOtp}
                className="text-xs text-cyellow hover:underline font-semibold inline-flex items-center gap-1 cursor-pointer"
              >
                Already have OTP code? Enter Code <ArrowRight size={12} />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onExit}
            disabled={sendingOtp}
            className="w-full border-neutral-700 text-cwhite hover:bg-white/10 hover:text-white"
          >
            Exit
          </Button>
          <Button
            type="button"
            onClick={onSendOtp}
            disabled={sendingOtp || resendCountdown > 0}
            className="w-full bg-cyellow hover:bg-yellow-500 text-black font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingOtp ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sending OTP...</span>
              </>
            ) : resendCountdown > 0 ? (
              <span>Confirm ({formatCountdownDisplay(resendCountdown)})</span>
            ) : (
              <span>Confirm & Send OTP</span>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
